import styled from 'styled-components';

/**
 * Answers of one question, side by side. They keep their own width instead of
 * each taking a share of the form, and wrap once the line is full - no
 * questionnaire here offers more than three answers, so they stay on one line.
 */
export const RadioRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  margin-top: 8px;
  max-width: 46rem;

  > * {
    flex: 0 1 auto;
  }
`;

/** A select does not need the width of the whole form. */
export const NarrowInput = styled.div`
  max-width: 26rem;
  width: 100%;
`;

/** Note replacing an input that has nothing to offer yet. */
export const EmptyNote = styled.p`
  ${({ theme }) => theme && `color: ${theme.colors.gray[500]};`}
  font-size: 14px;
  margin: 0;
`;

/** The proxy of a sent application: who it is, and what they answered. */
export const ProxyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 16px;
  margin-top: 8px;
  font-weight: 600;
`;

/**
 * Whether the proxy confirmed the claim. Green with a checkmark once they did,
 * so a confirmed proxy is recognised without reading the label.
 */
export const ProxyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  ${({ approved, theme }) => {
    const colors = (theme && theme.colors) || {};
    if (approved === true) {
      return `background: #e4f4e8; color: ${colors.success || '#2e7d32'};`;
    }
    if (approved === false) {
      return `background: #fbe6e6; color: ${colors.error || '#c62828'};`;
    }
    return `background: #f2f2f2; color: ${(colors.gray && colors.gray[600]) || '#5c5c5c'};`;
  }}

  svg {
    width: 14px;
    height: 14px;
    fill: currentColor;
  }
`;

/** Who applied, read as label and value pairs above the answers. */
export const SummaryList = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 6px 16px;
  margin: 0;
  font-size: 14px;

  dt {
    ${({ theme }) => theme && `color: ${theme.colors.gray[500]};`}
  }

  dd {
    margin: 0;
    font-weight: 600;
  }
`;

/** Every status an application went through, oldest first. */
export const HistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-left: 2px solid ${({ theme }) => (theme && theme.colors && theme.colors.secondary) || '#f59c00'};

  li {
    position: relative;
    padding: 0 0 16px 16px;
    font-size: 14px;

    &:last-child {
      padding-bottom: 0;
    }
  }
`;

/** Date and author of one status change. */
export const HistoryMeta = styled.span`
  ${({ theme }) => theme && `color: ${theme.colors.gray[500]};`}
  font-size: 13px;
`;

/**
 * The reason behind a decision. Red belongs to a refusal only - an accepted
 * application saying something in red reads as a problem where there is none.
 */
export const HistoryReason = styled.div`
  font-size: 14px;
  color: ${({ refused, theme }) => (refused
    ? theme.colors.alerts.error.text
    : theme.colors.alerts.success.text)};
`;
